import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { WebinarCardArt } from '@shopmy/ui';
import { webinars as webinarUtils } from '@shopmy/universal';
import { toBlob } from 'html-to-image';

import { downloadImageUsingElementId } from '../../helpers/helpers';
import './WebinarZoomAssets.scss';

const { isTakeawayOverLimit, KEY_TAKEAWAY_MAX_CHARS, EVENT_OVERVIEW_MAX_CHARS } = webinarUtils;

// Frames are laid out at 1× and exported at 2× so the Zoom uploads stay sharp.
const CARD_SIZE = { width: 1287, height: 701 };
const OVERVIEW_SIZE = { width: 1440, height: 835 };
const TAKEAWAYS_SIZE = { width: 1440, height: 575 };
const EXPORT_PIXEL_RATIO = 2;
const TAKEAWAYS_FONT_MAX = 30;
const TAKEAWAYS_FONT_MIN = 22;
const TAKEAWAYS_BOTTOM_MARGIN = 24;

const WebinarZoomAssets = ({ webinar }) => {
  const [speaker] = webinar.speakers;
  const takeaways = webinar.keyTakeaways.filter(item => item.lead || item.copy);

  const cardMissing = cardMissingFields(webinar);
  const overviewMissing = [
    !speaker.name && 'the guest speaker name',
    !speaker.headshot && 'a guest headshot',
    !webinar.description && 'the event overview',
    webinar.description.length > EVENT_OVERVIEW_MAX_CHARS && `an event overview of ${EVENT_OVERVIEW_MAX_CHARS} characters or fewer`
  ].filter(Boolean);
  const takeawaysMissing = [
    !takeaways.length && 'at least one key takeaway',
    takeaways.some(isTakeawayOverLimit) && `takeaways of ${KEY_TAKEAWAY_MAX_CHARS} characters or fewer`
  ].filter(Boolean);

  return (
    <div className='wb-zoom-assets'>
      <ZoomAsset
        id={frameId(webinar.slug, 'card')}
        label='Zoom card'
        helper='Same layout as the live card on shopmy.us. Downloads at 2574 × 1402.'
        missing={cardMissing}
        size={CARD_SIZE}
      >
        <CardFrame id={frameId(webinar.slug, 'card')} webinar={webinar} />
      </ZoomAsset>
      <ZoomAsset
        id={frameId(webinar.slug, 'overview')}
        label='Event Overview image'
        helper='Guest Speaker and Event Overview are fixed labels. Downloads at 2880 × 1670.'
        missing={overviewMissing}
        size={OVERVIEW_SIZE}
      >
        <EventOverviewFrame id={frameId(webinar.slug, 'overview')} webinar={webinar} speaker={speaker} />
      </ZoomAsset>
      <ZoomAsset
        id={frameId(webinar.slug, 'takeaways')}
        label='Key Takeaways image'
        helper='Bold lead-ins come from the takeaway fields above. Downloads at 2880 × 1150.'
        missing={takeawaysMissing}
        size={TAKEAWAYS_SIZE}
      >
        <KeyTakeawaysFrame id={frameId(webinar.slug, 'takeaways')} takeaways={takeaways} />
      </ZoomAsset>
    </div>
  );
};

WebinarZoomAssets.propTypes = {
  webinar: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    backgroundImage: PropTypes.string,
    description: PropTypes.string,
    speakers: PropTypes.arrayOf(
      PropTypes.shape({ name: PropTypes.string, handle: PropTypes.string, tier: PropTypes.string, headshot: PropTypes.string })
    ).isRequired,
    keyTakeaways: PropTypes.arrayOf(PropTypes.shape({ lead: PropTypes.string, copy: PropTypes.string }))
  }).isRequired
};

export default WebinarZoomAssets;

export const cardMissingFields = webinar => {
  const [speaker] = webinar.speakers;
  return [
    !webinar.title && 'the event name',
    !speaker.name && 'the guest speaker name',
    !speaker.headshot && 'a guest headshot',
    !webinar.backgroundImage && 'a stock background'
  ].filter(Boolean);
};

// The stored card is the same frame the Download button exports, read from the editor's DOM.
export const exportCardPng = slug => toBlob(document.getElementById(frameId(slug, 'card')), { cacheBust: true, pixelRatio: EXPORT_PIXEL_RATIO });

// The download helper names the file from the text before the first underscore.
const frameId = (slug, frame) => `${slug || 'webinar'}-${frame}_zoom`;

const ZoomAsset = ({ id, label, helper, missing, size, children }) => {
  const [previewRef, scale] = useFitScale(size.width);
  const [exporting, setExporting] = useState(false);

  const exportPng = async () => {
    setExporting(true);
    try {
      await downloadImageUsingElementId(id, { pixelRatio: EXPORT_PIXEL_RATIO });
    } catch {
      window.ALERT.error(`Could not export the ${label}. Please try again.`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className='wb-zoom-asset'>
      <div className='wb-zoom-asset-head'>
        <div>
          <label>{label}</label>
          <div className='wb-form-helper'>{missing.length ? `Add ${formatList(missing)} to download this image.` : helper}</div>
        </div>
        <button type='button' className='wb-btn wb-btn--primary' onClick={exportPng} disabled={exporting || missing.length > 0}>
          {exporting ? 'Exporting...' : 'Download PNG'}
        </button>
      </div>
      <div className='wb-zoom-asset-preview' ref={previewRef} style={{ height: Math.round(size.height * scale) }}>
        <div className='wb-zoom-asset-scaler' style={{ transform: `scale(${scale})` }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// The frame keeps its export size; only the preview around it shrinks to the editor width.
const useFitScale = frameWidth => {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.5);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setScale(Math.min(1, el.clientWidth / frameWidth));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [frameWidth]);

  return [ref, scale];
};

const CardFrame = ({ id, webinar }) => (
  <div id={id} style={CARD_SIZE}>
    <WebinarCardArt webinar={webinar} crossOrigin='anonymous' />
  </div>
);

const EventOverviewFrame = ({ id, webinar, speaker }) => {
  const credit = formatSpeakerCredit(speaker.handle, speaker.tier);
  return (
    <div id={id} className='wb-zoom-overview' style={OVERVIEW_SIZE}>
      <div className='overview-photo'>
        {speaker.headshot && (
          <>
            <img className='overview-photo-image' src={speaker.headshot} crossOrigin='anonymous' alt='' />
            <div className='overview-photo-blur'>
              <img src={speaker.headshot} crossOrigin='anonymous' alt='' />
            </div>
          </>
        )}
        <div className='overview-photo-shade' />
        <div className='overview-eyebrow'>Guest Speaker</div>
        <div className='overview-speaker'>
          <div className='overview-name'>{speaker.name}</div>
          {credit && <div className='overview-credit'>{credit}</div>}
        </div>
      </div>
      <div className='overview-copy'>
        <div className='overview-title'>Event Overview</div>
        <div className='overview-body'>{webinar.description.slice(0, EVENT_OVERVIEW_MAX_CHARS)}</div>
      </div>
    </div>
  );
};

const KeyTakeawaysFrame = ({ id, takeaways }) => {
  const listRef = useRef(null);
  const contentKey = JSON.stringify(takeaways);
  const [fit, setFit] = useState({ key: contentKey, fontSize: TAKEAWAYS_FONT_MAX });
  const fontSize = fit.key === contentKey ? fit.fontSize : TAKEAWAYS_FONT_MAX;
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    document.fonts?.ready.then(() => setFontsReady(true));
  }, []);

  // Four three-row items overrun the frame at 30px, so the list steps down until it fits.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || fontSize <= TAKEAWAYS_FONT_MIN) return;
    if (list.offsetTop + list.offsetHeight > TAKEAWAYS_SIZE.height - TAKEAWAYS_BOTTOM_MARGIN) {
      setFit({ key: contentKey, fontSize: fontSize - 2 });
    }
  }, [fontSize, contentKey, fontsReady]);

  return (
    <div id={id} className='wb-zoom-takeaways' style={TAKEAWAYS_SIZE}>
      <div className='takeaways-title'>Key Takeaways</div>
      <ol ref={listRef} className='takeaways-list' style={{ fontSize }}>
        {takeaways.map((takeaway, index) => (
          <li key={index}>
            <span className='takeaway-number'>{index + 1}.</span>
            <span className='takeaway-text'>
              {takeaway.lead && <strong>{takeaway.lead} </strong>}
              {takeaway.copy}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

const formatList = items => {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const formatSpeakerCredit = (handle, tier) => {
  const handleText = handle ? `@${handle.replace(/^@+/, '')}` : '';
  return [handleText, tier].filter(Boolean).join(' • ');
};
